module Main where

import Prelude

import Data.Array (filter)
import Data.Array as Array
import Data.Date (Date, Month(..), Weekday(..), Year, adjust, canonicalDate, lastDayOfMonth, month, weekday, year)
import Data.Date as Date
import Data.Enum (fromEnum, succ, toEnum)
import Data.Foldable (foldMap, foldl)
import Data.Int (toNumber)
import Data.Map as Map
import Data.Maybe (Maybe(..))
import Data.Maybe as Maybe
import Data.Time.Duration (Days(..))
import Data.Time.Duration as Duration
import Data.Traversable (sequence)
import Data.Tuple (Tuple)
import Data.Tuple as Tuple
import Data.Tuple.Nested ((/\))
import Effect (Effect)
import Effect.Exception as Exception
import Effect.Now (nowDate)
import Holidays (Holiday, holidays, isHoliday)
import Intl.NumberFormat (Currency(..), Locale(..), formatCurrency)
import React.Basic.DOM as R
import React.Basic.DOM.Client (createRoot, renderRoot)
import React.Basic.Events (handler_)
import React.Basic.Hooks (Component, component)
import React.Basic.Hooks as React
import Web.DOM.NonElementParentNode (getElementById)
import Web.HTML (window)
import Web.HTML.HTMLDocument (toNonElementParentNode)
import Web.HTML.Window (document)

type Charge =
  { description :: String
  , amount :: Number
  }

type SalaryBreakdown =
  { totalAmount :: Number
  , taxes :: Number
  , dailyTransportationCosts :: Number
  , transportationCosts :: Number
  , workDayCount :: Int
  , baseSalary :: Number
  , additionalCharges :: Array Charge
  , monthHolidays :: Array Holiday
  }

officialCategorySalary :: Date -> Number
officialCategorySalary date = case sequence [ toEnum 2024, toEnum 2025, toEnum 2026 ] of
  Just [ y2024, y2025, y2026 ]
    | Date.year date >= y2026 && Date.month date > January -> 1668.80
    | Date.year date == y2026 || (Date.year date >= y2025 && Date.month date > January) -> 1518.00
    | Date.year date == y2025 || (Date.year date >= y2024 && Date.month date > January) -> 1439.23
    | otherwise -> 1370.7

  _ ->
    1370.7

baseSalary :: Date -> Number
baseSalary date = officialCategorySalary date * 2.0

taxPercentage :: Date -> Number
taxPercentage date = case ((toEnum 2026 /\ February)) of
  (Just y2026 /\ feb)
    | Date.year date > y2026 -> 0.0
    | Date.year date == y2026 && Date.month date > feb -> 0.0
    | otherwise -> 0.0767
  _ -> 0.0767

dailyTransportationCosts :: Date -> Number
dailyTransportationCosts refDate = case toEnum 2026 of
  Just y2026 ->
    if Date.year refDate < y2026 then 17.1
    else 18.80
  _ ->
    17.1

monthStart :: Date -> Maybe Date
monthStart date = canonicalDate (year date) (month date) <$> (toEnum 1)

monthEnd :: Date -> Date
monthEnd date = canonicalDate (year date) (month date) day
  where
  day = lastDayOfMonth (year date) (month date)

daysInMonth :: Date -> Date -> Array Date
daysInMonth start end = go (Just start) []
  where
  go Nothing ds = ds

  go (Just d) ds
    | d == end = Array.snoc ds end
    | otherwise = go (adjust (Days 1.0) d) (Array.snoc ds d)

isWorkDay :: Date -> Boolean
isWorkDay d = weekday d /= Saturday && weekday d /= Sunday && not (isHoliday d)

workDaysInMonth :: Date -> Date -> Array Date
workDaysInMonth s = filter isWorkDay <<< daysInMonth s

monthRange :: Date -> Maybe (Tuple Date Date)
monthRange date = do
  let
    lastDay = monthEnd date

  if (Date.diff lastDay date) <= (Duration.Days 3.0) then
    monthRange =<< (succ lastDay)
  else do
    let firstDay = monthStart date

    (/\) <$> firstDay <*> Just lastDay

monthsSalary :: Tuple Date Date -> SalaryBreakdown
monthsSalary (firstDay /\ lastDay) =
  { totalAmount: baseSalary firstDay - taxes + transportationCosts +
      (foldl (\sum { amount } -> sum + amount) 0.0 additionalCharges)
  , taxes
  , dailyTransportationCosts: dailyTransportationCosts firstDay
  , transportationCosts
  , workDayCount
  , baseSalary: baseSalary firstDay
  , monthHolidays
  , additionalCharges
  }
  where
  workDayCount = Array.length (workDaysInMonth firstDay lastDay)

  transportationCosts = toNumber workDayCount * dailyTransportationCosts firstDay

  taxes = officialCategorySalary firstDay * taxPercentage firstDay

  monthHolidays = foldMap monthHoliday (daysInMonth firstDay lastDay)
    where
    yearHolidays = Maybe.fromMaybe Map.empty $ Map.lookup (year firstDay) holidays
    monthHoliday d = case Map.lookup d yearHolidays of
      Just holiday -> [ holiday ]
      Nothing -> []

  additionalCharges =
    if Date.month firstDay == Date.December then
      [ { description: "13º salário"
        , amount: baseSalary firstDay
        }
      ]
    else []

monthSelector
  :: ( ( Maybe (Tuple Date Date)
         -> Maybe (Tuple Date Date)
       )
       -> Effect Unit
     )
  -> Date
  -> React.JSX
monthSelector setRange refDate =
  R.div
    { className: "w-full bg-gray-500 text-white text-4xl"
    , children:
        [ R.div
            { className: "flex p-4"
            , children:
                [ R.div
                    { className: "basis-lg text-4xl cursor-pointer"
                    , onClick: handler_ (moveMonths (-1.0))
                    , children: [ R.text "←" ]
                    }
                , R.div
                    { className: "grow shrink mx-4 text-center"
                    , children: [ R.text $ monthName <> " " <> show (fromEnum $ year refDate) ]
                    }
                , R.div
                    { className: "basis-lg text-4xl text-right cursor-pointer"
                    , onClick: handler_ (moveMonths 1.0)
                    , children: [ R.text "→" ]
                    }
                ]
            }
        ]
    }
  where
  monthName = case month refDate of
    January -> "Janeiro"
    February -> "Fevereiro"
    March -> "Março"
    April -> "Abril"
    May -> "Maio"
    June -> "Junho"
    July -> "Julho"
    August -> "Agosto"
    September -> "Setembro"
    October -> "Outubro"
    November -> "Novembro"
    December -> "Dezembro"

  moveMonths amount =
    setRange
      ( \range -> do
          start /\ end <- range
          newEnd <- monthEnd <$> adjust (Days amount) (if amount > 0.0 then end else start)
          newStart <- monthStart newEnd
          pure $ newStart /\ newEnd
      )

formatAmount :: Number -> String
formatAmount = formatCurrency (Locale "pt-BR") (Currency "BRL")

totalAmount :: SalaryBreakdown -> React.JSX
totalAmount breakdown =
  R.div
    { className: "flex items-center justify-center h-[200px] border-t border-b border-gray-300 text-5xl md:text-6xl text-red-600 font-medium px-8"
    , children: [ R.text $ formatAmount breakdown.totalAmount ]
    }

lineItems :: SalaryBreakdown -> React.JSX
lineItems breakdown =
  R.div
    { className: "mx-6 text-xl"
    , children:
        [ R.ul
            { className: "p-4 list-none"
            , children:
                [ lineItem "Salário base" breakdown.baseSalary
                , lineItem ("Passagens " <> " (" <> show breakdown.workDayCount <> " dias úteis × " <> formatAmount breakdown.dailyTransportationCosts <> ")")
                    breakdown.transportationCosts
                , lineItem "Impostos" breakdown.taxes
                ] <> additionalCharges
            }
        ]
    }
  where
  lineItem desc amount =
    R.li
      { className: "font-extralight mb-3"
      , children:
          [ R.span { className: "font-normal", children: [ R.text (desc <> ": ") ] }
          , R.text $ formatAmount amount
          ]
      }

  additionalCharges = breakdown.additionalCharges
    <#> \charge -> lineItem charge.description charge.amount

monthHolidaysView :: SalaryBreakdown -> React.JSX
monthHolidaysView { monthHolidays } =
  if Array.null monthHolidays then R.text ""
  else
    R.div
      { children:
          [ R.h4
              { className: "p-4 text-center bg-gray-500 text-white mb-4"
              , children: [ R.text "Feriados" ]
              }
          , R.div
              { className: "mx-6 text-xl"
              , children:
                  [ R.ul
                      { className: "px-4 list-none"
                      , children: holidayItems
                      }
                  ]
              }
          ]
      }
  where
  formatDate d = (show $ fromEnum (Date.day d)) <> "/" <> (show $ fromEnum (Date.month d)) <> "/" <> (show $ fromEnum (Date.year d))
  holidayItems =
    monthHolidays
      <#>
        ( \{ name, date } ->
            R.li
              { className: "font-extralight mb-3"
              , children:
                  [ R.span { className: "font-normal", children: [ R.text (formatDate date) ] }
                  , R.text " — "
                  , R.text name
                  ]
              }
        )

mkApp :: Component Date
mkApp =
  component "App" \currentDate -> React.do
    selectedRange /\ setSelectedRange <- React.useState (monthRange currentDate)
    pure
      $ case selectedRange of
          Just range -> do
            let
              breakdown = monthsSalary range
            R.div
              { className: "font-outfit"
              , children:
                  [ monthSelector setSelectedRange (Tuple.fst range)
                  , R.h2
                      { className: "text-4xl text-center p-8 font-medium"
                      , children: [ R.text "Delminha embolsa" ]
                      }
                  , totalAmount breakdown
                  , lineItems breakdown
                  , monthHolidaysView breakdown
                  ]
              }
          Nothing -> R.text "Nothing to see here"

main :: Effect Unit
main = do
  container <- getElementById "app" =<< (toNonElementParentNode <$> (document =<< window))
  case container of
    Nothing -> Exception.throw "Missing container element"
    Just c -> do
      root <- createRoot c
      layout <- mkApp <*> nowDate

      renderRoot root layout
